import * as React from "react";
import { Tooltip as TooltipPrimitive } from "radix-ui";

import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-media-query";
import {
  Drawer,
  DrawerHeader,
  DrawerOverlay,
  DrawerPanel,
  DrawerPortal,
  DrawerTitle,
} from "@/components/ui/drawer";

type Mode =
  | { mode: "desktop" }
  | { mode: "mobile"; setOpen: (v: boolean) => void };

const TooltipModeContext = React.createContext<Mode>({ mode: "desktop" });

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);

  if (isMobile) {
    return (
      <TooltipModeContext.Provider value={{ mode: "mobile", setOpen }}>
        <Drawer open={open} onOpenChange={setOpen}>
          {children}
        </Drawer>
      </TooltipModeContext.Provider>
    );
  }
  return (
    <TooltipModeContext.Provider value={{ mode: "desktop" }}>
      <TooltipPrimitive.Root data-slot="tooltip" {...props}>
        {children}
      </TooltipPrimitive.Root>
    </TooltipModeContext.Provider>
  );
}

function TooltipTrigger({
  asChild,
  onClick,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  const ctx = React.useContext(TooltipModeContext);

  if (ctx.mode === "mobile") {
    const handleClick: React.MouseEventHandler<HTMLElement> = (e) => {
      e.preventDefault();
      e.stopPropagation();
      ctx.setOpen(true);
      onClick?.(e as React.MouseEvent<HTMLButtonElement>);
    };

    if (asChild && React.isValidElement(children)) {
      const child = children as React.ReactElement<{
        onClick?: React.MouseEventHandler<HTMLElement>;
      }>;
      const childOnClick = child.props.onClick;
      return React.cloneElement(child, {
        ...props,
        onClick: (e: React.MouseEvent<HTMLElement>) => {
          handleClick(e);
          childOnClick?.(e);
        },
      });
    }
    return (
      <button
        type="button"
        data-slot="tooltip-trigger"
        onClick={handleClick}
        {...(props as React.ComponentProps<"button">)}
      >
        {children}
      </button>
    );
  }

  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      asChild={asChild}
      onClick={onClick}
      {...props}
    >
      {children}
    </TooltipPrimitive.Trigger>
  );
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  title,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  const ctx = React.useContext(TooltipModeContext);

  if (ctx.mode === "mobile") {
    return (
      <DrawerPortal>
        <DrawerOverlay
          onClick={(e) => {
            e.stopPropagation();
            ctx.setOpen(false);
          }}
        />
        <DrawerPanel
          data-slot="tooltip-content"
          onClick={(e) => e.stopPropagation()}
        >
          <DrawerHeader>
            <DrawerTitle className="text-left">
              {title || "Information"}
            </DrawerTitle>
          </DrawerHeader>
          <div className={cn("px-4 pb-8 text-sm", className)}>{children}</div>
        </DrawerPanel>
      </DrawerPortal>
    );
  }

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          "z-50 w-fit origin-(--radix-tooltip-content-transform-origin) animate-in rounded-md bg-foreground px-3 py-1.5 text-xs text-balance text-background fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-foreground fill-foreground" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
